/**
 * passkey-enroll — the enrolment offer as one control at three densities.
 *
 * `prompt` is the moment after a code sign-in, inside show()'s own surface,
 * where nobody asked — so it states the whole offer. `row` is a line in the
 * device preferences, where someone arrived on purpose: the fact on the left,
 * the one act on the right, riding the same line.
 * `utility` is the compact fingerprint action beside sign-out.
 *
 * Data down, intents up, no I/O. The product passes the device's one `phase`
 * — a running or failed attempt outranks the standing fact, so a redo on a
 * device that already holds a passkey reads working, then done or error — and
 * answers three events: `passkey-enroll` (start a ceremony), and at prompt
 * density `passkey-enroll-defer` ("Not now") and `passkey-enroll-done`
 * ("Done", or the payoff having been read). The product gates the mount on
 * `passkeySupported$`: without a platform authenticator there is nothing
 * here to render.
 *
 * The error phase is also the cancel phase — a closed OS prompt reaches the
 * product as the same DOMException a real failure does — so the wording
 * states the outcome rather than asserting a fault, and the refusal renders
 * in the control, never at a toast that would outlive the surface.
 */
import { SchmancyElement, schmancyElement } from '@mhmo91/schmancy/mixins';
import { confirm } from '@mhmo91/schmancy/overlay';
import { css, html, type PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { when } from 'lit/directives/when.js';
import {
  Subject,
  exhaustMap,
  filter,
  from,
  merge,
  pairwise,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { de } from './catalog.de.ts';
import { en } from './catalog.en.ts';

/** How long the payoff stays on screen before the prompt asks to close: long
 *  enough to read one line, short enough that nobody reaches for the button. */
export const PAYOFF_MS = 1800;

export type PasskeyLanguage = 'de' | 'en';
export type PasskeyEnrollDensity = 'prompt' | 'row' | 'utility';
/** What the control renders. `done` is the standing fact; the rest is this tab's attempt. */
export type PasskeyEnrollPhase = 'idle' | 'working' | 'done' | 'error';

const CATALOG = { de, en };

@schmancyElement('passkey-enroll')
export class PasskeyEnroll extends SchmancyElement {
  // At row density the element sits inside the preferences panel's own
  // column; at prompt density the overlay lays its content out as a grid item
  // and pads nothing — so the measure and the padding belong to the root
  // element inside, never to the host.
  static styles = [
    css`
      :host {
        display: contents;
      }
    `,
  ];

  @property({ type: String }) density: PasskeyEnrollDensity = 'row';
  @property({ type: String }) language: PasskeyLanguage = 'en';
  /** The address the passkey is bound to — the relying-party id. */
  @property({ type: String }) host = location.hostname;
  @property({ type: String }) phase: PasskeyEnrollPhase = 'idle';

  private readonly enroll$ = new Subject<void>();
  private readonly redo$ = new Subject<void>();
  private readonly phase$ = new Subject<PasskeyEnrollPhase>();

  private readonly enroll = () => this.enroll$.next();
  private readonly redo = () => this.redo$.next();
  private readonly defer = () =>
    this.dispatchEvent(new CustomEvent('passkey-enroll-defer', { bubbles: true, composed: true }));
  private readonly done = () =>
    this.dispatchEvent(new CustomEvent('passkey-enroll-done', { bubbles: true, composed: true }));

  override connectedCallback() {
    super.connectedCallback();
    merge(
      // One intent per answer: the product's phase arrives a render later than
      // the click, so the window stays shut until it moves. The OS prompt is
      // modal above the whole browser, so a gesture while it is open has
      // nothing to start.
      this.enroll$.pipe(
        filter(() => this.phase !== 'working'),
        exhaustMap(() => {
          this.dispatchEvent(new CustomEvent('passkey-enroll', { bubbles: true, composed: true }));
          return this.phase$.pipe(take(1));
        }),
      ),
      // Replacing the passkey this device offers first is worth a pause.
      this.redo$.pipe(
        exhaustMap(() =>
          from(
            confirm({
              title: CATALOG[this.language].redo.title,
              message: CATALOG[this.language].redo.body(this.host),
              confirmText: CATALOG[this.language].redo.confirm,
              cancelText: CATALOG[this.language].redo.cancel,
            }),
          ),
        ),
        filter(Boolean),
        tap(this.enroll),
      ),
      // The payoff is a beat, not a screen. Keyed on the ceremony's own
      // answer — the phase moving off working — not the standing fact, so a
      // prompt opened on a device that already reads done holds still.
      this.phase$.pipe(
        startWith(this.phase),
        pairwise(),
        filter(([was, is]) => was === 'working' && is === 'done' && this.density === 'prompt'),
        switchMap(() => timer(PAYOFF_MS)),
        tap(this.done),
      ),
    )
      .pipe(takeUntil(this.disconnecting))
      .subscribe();
  }

  override updated(changed: PropertyValues<this>) {
    if (changed.has('phase')) this.phase$.next(this.phase);
  }

  override render() {
    const copy = CATALOG[this.language];
    const status = this.phase === 'done' ? copy.status.done(this.host) : copy.status[this.phase];

    return choose(this.density, [
      [
        'utility',
        () => html`
          <schmancy-icon-button
            icon="fingerprint"
            variant="text"
            color=${this.phase === 'error' ? 'error' : this.phase === 'idle' ? 'neutral' : 'success'}
            .busy=${this.phase === 'working'}
            title=${this.phase === 'done' ? copy.redo.confirm : copy.row[this.phase]}
            @click=${this.phase === 'done' ? this.redo : this.phase === 'working' ? undefined : this.enroll}
          ></schmancy-icon-button>
          <schmancy-visually-hidden role="status" aria-live="polite">
            ${when(this.phase !== 'idle', () => status)}
          </schmancy-visually-hidden>
        `,
      ],
      [
        'row',
        () => html`
          <div class="flex items-center gap-3">
            <schmancy-icon
              class=${this.classMap({
                'text-base': true,
                'text-surface-onVariant': this.phase !== 'error',
                'text-error-default': this.phase === 'error',
              })}
              >fingerprint</schmancy-icon
            >
            <schmancy-typography
              type="body"
              token="sm"
              class=${this.classMap({
                'flex-1 min-w-0': true,
                'text-surface-onVariant': this.phase !== 'error',
                'text-error-default': this.phase === 'error',
              })}
              >${this.phase === 'done' ? copy.row.done(this.host) : copy.row[this.phase]}</schmancy-typography
            >
            <schmancy-button
              class="shrink-0"
              variant="text"
              size="sm"
              ?busy=${this.phase === 'working'}
              @click=${this.phase === 'done' ? this.redo : this.enroll}
            >
              ${copy.rowAction[this.phase]}
            </schmancy-button>
          </div>
          <schmancy-visually-hidden role="status" aria-live="polite">
            ${when(this.phase !== 'idle', () => status)}
          </schmancy-visually-hidden>
        `,
      ],
      [
        'prompt',
        () => html`
          <div class="box-border w-md max-w-full grid gap-5 p-6">
            <div class="grid gap-1 min-w-0">
              <schmancy-typography type="headline" token="sm">
                ${this.phase === 'done' ? copy.heading.done : copy.heading.offer(this.host)}
              </schmancy-typography>
              <schmancy-typography type="body" token="md" class="text-surface-onVariant">
                ${copy.body}
              </schmancy-typography>
            </div>

            <!-- One idle line is reserved; a longer status wraps. Only the
                 live region announces, so nothing is spoken twice. -->
            <schmancy-typography
              type="body"
              token="sm"
              aria-hidden="true"
              class=${this.classMap({
                'text-surface-onVariant': this.phase !== 'error',
                'text-error-default': this.phase === 'error',
              })}
              >${status}</schmancy-typography
            >
            <schmancy-visually-hidden role="status" aria-live="polite">
              ${when(this.phase !== 'idle', () => status)}
            </schmancy-visually-hidden>

            <div class="flex flex-wrap items-center justify-start gap-2">
              <!-- Present in every phase but the last, at full readability
                   and live while the ceremony runs: a repeated offer whose
                   "no" has to be hunted for teaches reflexive dismissal. -->
              ${when(
                this.phase !== 'done',
                () => html`
                  <schmancy-button variant="outlined" size="md" @click=${this.defer}>
                    ${copy.decline}
                  </schmancy-button>
                `,
              )}
              <schmancy-button
                variant="filled"
                size="md"
                ?busy=${this.phase === 'working'}
                @click=${this.phase === 'done' ? this.done : this.enroll}
              >
                ${copy.action[this.phase]}
              </schmancy-button>
            </div>
          </div>
        `,
      ],
    ]);
  }
}

/** The dialog's name — `role="dialog"` takes none from its content. */
export function passkeyEnrollAriaLabel(language: PasskeyLanguage, host: string): string {
  return CATALOG[language].ariaLabel(host);
}

declare global {
  interface HTMLElementTagNameMap {
    'passkey-enroll': PasskeyEnroll;
  }
  interface HTMLElementEventMap {
    'passkey-enroll': CustomEvent<void>;
    'passkey-enroll-defer': CustomEvent<void>;
    'passkey-enroll-done': CustomEvent<void>;
  }
}
